import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Loader2, Database, Mail, Users, FileText, ArrowRight, XCircle, Check, Globe, ShieldCheck, Copy, RefreshCw } from 'lucide-react';
import { registrationApi } from '../../services/saasApi';
import { toast } from 'sonner';

const Step5Confirmation = ({ formData, loading, onNext, sessionId, isSocial }) => {
  const { t, i18n } = useTranslation();
  const baseDomain = process.env.REACT_APP_BASE_DOMAIN || 'mawered.local';
  const startedRef = useRef(false);
  const [copied, setCopied] = useState(false);
  const [resending, setResending] = useState(false);

  const handleCopyUrl = () => {
    const url = `https://${formData.subdomain}.${baseDomain}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleResendEmail = async () => {
    setResending(true);
    try {
      await registrationApi.resendWelcomeEmail(formData.admin_email, formData.subdomain);
      toast.success(t('registration.step5.success.resendSuccess'));
    } catch {
      toast.error(t('registration.step5.success.resendError'));
    } finally {
      setResending(false);
    }
  };

  const [provisioningSteps, setProvisioningSteps] = useState([
    { id: 1, titleKey: 'registration.step5.creating.step1', completed: false, icon: Users },
    { id: 2, titleKey: 'registration.step5.creating.step2', completed: false, icon: Database },
    { id: 3, titleKey: 'registration.step5.creating.step3', completed: false, icon: Globe },
    { id: 4, titleKey: 'registration.step5.creating.step4', completed: false, icon: ShieldCheck },
    { id: 5, titleKey: 'registration.step5.creating.step5', completed: false, icon: FileText },
    { id: 6, titleKey: 'registration.step5.creating.step6', completed: false, icon: Mail },
  ]);

  const [currentStepId, setCurrentStepId] = useState(1);
  const [provisioningComplete, setProvisioningComplete] = useState(false);
  const [provisioningFailed, setProvisioningFailed] = useState(false);

  useEffect(() => {
    if (sessionId && !startedRef.current) {
      startedRef.current = true;
      startProvisioning();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const delay = (ms) => new Promise((r) => setTimeout(r, ms));

  // Provisioning runs server-side in the background (DB + migrations + seed +
  // email ≈ several minutes). step5 returns immediately with organization_id;
  // we then poll /register/status until "ready" or "failed". Short polls are
  // immune to the CDN edge timeout that used to kill the single long request.
  const POLL_INTERVAL_MS = 4000;
  const POLL_TIMEOUT_MS = 10 * 60 * 1000;

  const pollUntilReady = async (organizationId, subdomain) => {
    const startedAt = Date.now();
    let consecutiveErrors = 0;
    while (Date.now() - startedAt < POLL_TIMEOUT_MS) {
      await delay(POLL_INTERVAL_MS);
      try {
        const { status } = await registrationApi.getProvisioningStatus(organizationId, subdomain);
        consecutiveErrors = 0;
        if (status === 'ready') return true;
        if (status === 'failed') return false;
      } catch {
        // Transient network/CDN error — keep polling unless it persists
        consecutiveErrors += 1;
        if (consecutiveErrors >= 15) return false;
      }
    }
    return false;
  };

  const startProvisioning = async () => {
    try {
      // Real API call — creates the org and schedules background provisioning
      const response = await onNext();
      if (!response) return; // Stripe redirect in progress

      // Animate steps 1–5 while the backend works (perceived progress)
      const animatedSteps = [1, 2, 3, 4, 5];
      for (const id of animatedSteps) {
        setCurrentStepId(id);
        await delay(2200);
        setProvisioningSteps((prev) => prev.map((s) => s.id === id ? { ...s, completed: true } : s));
      }
      setCurrentStepId(6);

      // Real completion gate — poll until the tenant is actually provisioned
      const ready = await pollUntilReady(response.organization_id, response.subdomain);
      if (ready) {
        setProvisioningSteps((prev) => prev.map((s) => ({ ...s, completed: true })));
        setProvisioningComplete(true);
        toast.success(t('registration.toasts.accountCreated'));
      } else {
        setProvisioningFailed(true);
        setProvisioningSteps((prev) => prev.map((s) => ({ ...s, completed: false })));
      }
    } catch {
      setProvisioningFailed(true);
      setProvisioningSteps((prev) => prev.map((s) => ({ ...s, completed: false })));
    }
  };

  const getDateLocale = () => {
    switch (i18n.language) {
      case 'ar': return 'ar-TN';
      case 'en': return 'en-US';
      default: return 'fr-FR';
    }
  };

  if (provisioningFailed) {
    return (
      <div className="p-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-5"
          style={{ backgroundColor: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)" }}>
          <XCircle className="w-8 h-8" style={{ color: "#ef4444" }} />
        </div>
        <h2 className="text-xl font-semibold mb-2" style={{ color: "var(--lm-ink)", letterSpacing: "-0.4px" }}>
          {t('registration.step5.error.title')}
        </h2>
        <p className="text-sm mb-1" style={{ color: "var(--lm-ink-2)" }}>
          {t('registration.step5.error.message')}
        </p>
        <p className="text-xs mb-8" style={{ color: "var(--lm-ink-3)" }}>
          {t('registration.step5.error.contact')}{' '}
          <a href="mailto:support@mawered.com" className="underline" style={{ color: "#5e6ad2" }}>
            support@mawered.com
          </a>
        </p>
        <button
          onClick={() => { window.location.href = '/register'; }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors"
          style={{ backgroundColor: "#5e6ad2", color: "#fff" }}
          onMouseEnter={e => (e.currentTarget.style.backgroundColor = "#828fff")}
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = "#5e6ad2")}
        >
          {t('registration.step5.error.retry')}
        </button>
      </div>
    );
  }

  if (!provisioningComplete || !formData.login_url) {
    return (
      <div className="p-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl mb-5"
            style={{ backgroundColor: "rgba(94,106,210,0.12)", border: "1px solid rgba(94,106,210,0.3)" }}>
            <Loader2 className="w-7 h-7 animate-spin" style={{ color: "#5e6ad2" }} />
          </div>
          <h2 className="text-xl font-semibold mb-1" style={{ color: "var(--lm-ink)", letterSpacing: "-0.4px" }}>
            {t('registration.step5.creating.title')}
          </h2>
          <p className="text-sm" style={{ color: "var(--lm-ink-2)" }}>
            {t('registration.step5.creating.subtitle')}
          </p>
        </div>

        <div className="space-y-2.5 mb-6">
          {provisioningSteps.map((step) => {
            const Icon = step.icon;
            const isCurrent = step.id === currentStepId && !step.completed;
            const isPending = step.id > currentStepId && !step.completed;
            return (
              <div key={step.id}
                className="flex items-center gap-3.5 px-4 py-3 rounded-xl transition-all duration-500"
                style={{
                  backgroundColor: step.completed ? "rgba(39,166,68,0.06)" : isCurrent ? "rgba(94,106,210,0.07)" : "transparent",
                  border: `1px solid ${step.completed ? "rgba(39,166,68,0.25)" : isCurrent ? "rgba(94,106,210,0.25)" : "transparent"}`,
                  opacity: isPending ? 0.4 : 1,
                }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300"
                  style={{
                    backgroundColor: step.completed ? "rgba(39,166,68,0.15)" : isCurrent ? "rgba(94,106,210,0.15)" : "var(--lm-s2)",
                    border: `1px solid ${step.completed ? "rgba(39,166,68,0.3)" : isCurrent ? "rgba(94,106,210,0.3)" : "var(--lm-border)"}`,
                  }}>
                  {step.completed
                    ? <Check className="w-3.5 h-3.5" style={{ color: "#27a644" }} />
                    : isCurrent
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: "#5e6ad2" }} />
                      : <Icon className="w-3.5 h-3.5" style={{ color: "var(--lm-ink-3)" }} />
                  }
                </div>
                <span className="text-sm font-medium transition-colors duration-300"
                  style={{ color: step.completed ? "#27a644" : isCurrent ? "var(--lm-ink)" : "var(--lm-ink-3)" }}>
                  {t(step.titleKey)}
                </span>
                {step.completed && (
                  <span className="ml-auto text-xs font-medium" style={{ color: "#27a644" }}>✓</span>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-center gap-2 text-xs" style={{ color: "var(--lm-ink-3)" }}>
          <div className="h-px flex-1" style={{ background: "linear-gradient(to right, transparent, var(--lm-border), transparent)" }} />
          <span>{t('registration.step5.creating.subtitle')}</span>
          <div className="h-px flex-1" style={{ background: "linear-gradient(to right, var(--lm-border), transparent)" }} />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Success header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl mb-5"
          style={{ backgroundColor: "rgba(39,166,68,0.12)", border: "1px solid rgba(39,166,68,0.3)" }}>
          <CheckCircle className="w-7 h-7" style={{ color: "#27a644" }} />
        </div>
        <h2 className="text-xl font-semibold mb-1" style={{ color: "var(--lm-ink)", letterSpacing: "-0.4px" }}>
          {t('registration.step5.success.title')}
        </h2>
        <p className="text-sm" style={{ color: "var(--lm-ink-2)" }}>
          {t('registration.step5.success.subtitle')}
        </p>
      </div>

      <div className="space-y-4 mb-8">
        {/* Company & subdomain */}
        <div className="p-5 rounded-xl" style={{ backgroundColor: "rgba(94,106,210,0.06)", border: "1px solid rgba(94,106,210,0.25)" }}>
          <p className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: "var(--lm-ink-3)" }}>
            {t('registration.step5.success.yourCompany')}
          </p>
          <p className="text-base font-semibold mb-3" style={{ color: "var(--lm-ink)" }}>{formData.company_name}</p>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs" style={{ color: "var(--lm-ink-2)" }}>{t('registration.step5.success.accessUrl')}</span>
            <code className="px-2.5 py-1 rounded text-xs font-mono" dir="ltr"
              style={{ backgroundColor: "var(--lm-s1)", border: "1px solid #23252a", color: "#828fff" }}>
              {formData.subdomain}.{baseDomain}
            </code>
            <button
              type="button"
              onClick={handleCopyUrl}
              title="Copier l'URL"
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors"
              style={{
                backgroundColor: copied ? "rgba(39,166,68,0.15)" : "var(--lm-s2)",
                border: `1px solid ${copied ? "rgba(39,166,68,0.4)" : "var(--lm-border)"}`,
                color: copied ? "#27a644" : "var(--lm-ink-3)",
              }}>
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? t('common.copied') : t('common.copy')}
            </button>
          </div>
        </div>

        {/* Trial info */}
        <div className="p-5 rounded-xl" style={{ backgroundColor: "var(--lm-s2)", border: "1px solid rgba(39,166,68,0.25)" }}>
          <div className="flex items-start gap-4">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "rgba(39,166,68,0.12)", border: "1px solid rgba(39,166,68,0.25)" }}>
              <CheckCircle className="w-4 h-4" style={{ color: "#27a644" }} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold mb-0.5" style={{ color: "var(--lm-ink)" }}>
                {t('registration.step5.success.trialActivated')}
              </p>
              <p className="text-xs mb-2" style={{ color: "var(--lm-ink-2)" }}>
                {t('registration.step5.success.trialDescription')}
              </p>
              {formData.trial_end_date && (
                <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>
                  <span style={{ color: "var(--lm-ink-2)" }}>{t('registration.step5.success.trialEndDate')}</span>{' '}
                  <span style={{ color: "var(--lm-ink)" }}>
                    {new Date(formData.trial_end_date).toLocaleDateString(getDateLocale(), { day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Email sent */}
        <div className="p-5 rounded-xl" style={{ backgroundColor: "var(--lm-s2)", border: "1px solid #23252a" }}>
          <div className="flex items-start gap-4">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "rgba(94,106,210,0.12)", border: "1px solid rgba(94,106,210,0.25)" }}>
              <Mail className="w-4 h-4" style={{ color: "#5e6ad2" }} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold mb-0.5" style={{ color: "var(--lm-ink)" }}>
                {t('registration.step5.success.welcomeEmailSent')}
              </p>
              <p className="text-xs" style={{ color: "var(--lm-ink-2)" }}>
                {t('registration.step5.success.credentialsSent')}{' '}
                <span style={{ color: "var(--lm-ink)" }}>{formData.admin_email}</span>
              </p>
              <p className="text-xs mt-1 mb-3" style={{ color: "var(--lm-ink-3)" }}>
                {t('registration.step5.success.checkInbox')}
              </p>
              <button
                type="button"
                onClick={handleResendEmail}
                disabled={resending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                style={{
                  backgroundColor: "var(--lm-s1)",
                  border: "1px solid var(--lm-border)",
                  color: "var(--lm-ink-2)",
                  opacity: resending ? 0.6 : 1,
                  cursor: resending ? "not-allowed" : "pointer",
                }}
                onMouseEnter={e => { if (!resending) { e.currentTarget.style.borderColor = "#5e6ad2"; e.currentTarget.style.color = "#828fff"; }}}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--lm-border)"; e.currentTarget.style.color = "var(--lm-ink-2)"; }}>
                <RefreshCw className={`w-3 h-3 ${resending ? 'animate-spin' : ''}`} />
                {resending
                  ? (t('registration.step5.success.resending') || 'Renvoi en cours…')
                  : (t('registration.step5.success.resendEmail') || 'Renvoyer l\'email')}
              </button>
            </div>
          </div>
        </div>

        {/* Next steps */}
        <div className="p-5 rounded-xl" style={{ backgroundColor: "var(--lm-s2)", border: "1px solid #23252a" }}>
          <p className="text-xs font-medium uppercase tracking-wider mb-4" style={{ color: "var(--lm-ink-3)" }}>
            {t('registration.step5.success.nextSteps')}
          </p>
          <ul className="space-y-3">
            {[
              { label: t('registration.step5.success.nextStep1'), href: formData.login_url },
              { label: t('registration.step5.success.nextStep2'), href: formData.login_url },
              { label: t('registration.step5.success.nextStep3'), href: null },
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-semibold"
                  style={{ backgroundColor: "rgba(94,106,210,0.15)", color: "#828fff" }}>
                  {i + 1}
                </div>
                {step.href ? (
                  <a href={step.href} className="text-sm transition-colors"
                    style={{ color: "var(--lm-ink-2)" }}
                    onMouseEnter={e => (e.currentTarget.style.color = "#828fff")}
                    onMouseLeave={e => (e.currentTarget.style.color = "var(--lm-ink-2)")}>
                    {step.label}
                    <ArrowRight className="w-3 h-3 inline ml-1 rtl:rotate-180" />
                  </a>
                ) : (
                  <span className="text-sm" style={{ color: "var(--lm-ink-2)" }}>{step.label}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* CTA */}
      <div className="text-center">
        <a
          href={formData.login_url}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold transition-colors"
          style={{ backgroundColor: "#5e6ad2", color: "#fff" }}
          onMouseEnter={e => (e.currentTarget.style.backgroundColor = "#828fff")}
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = "#5e6ad2")}
        >
          {isSocial ? t('registration.step5.success.accessAccountSocial', 'Accéder au tableau de bord') : t('registration.step5.success.accessAccount')}
          <ArrowRight className="w-4 h-4 rtl:rotate-180" />
        </a>
        <p className="mt-3 text-xs" style={{ color: "var(--lm-ink-3)" }}>
          {t('registration.step5.success.redirecting')}
        </p>
      </div>
    </div>
  );
};

export default Step5Confirmation;
